import type { ListCatalogItemsBrowserInput } from "@/api/contracts/catalog";
import type { SpecEditSearch } from "@/api/contracts/specifications";
import { DEFAULT_CATALOG_SORT, normalizeCatalogSearchTerm } from "@/lib/catalog-list-url-state";
import { showsLastRequestedItem } from "@/lib/last-requested-item";

// The Item Search island's rules (#811) — the pure half of the donor's
// `ItemSearchIsland` (`timmy/src/domains/items/islands/ItemSearchIsland/`) and
// the editor-side payload it observes (`SpecEditIsland/data/slice.ts:60-81`).
// The host (`components/specifications/item-search-island.tsx`) composes these
// over the page's Active selection, the URL and the catalog's list read.

/**
 * The donor's hardcoded page size (`item_search_island/router.py:142`):
 * "Divisible by 2, 3, and 4 columns."
 */
export const ISLAND_PAGE_SIZE = 12;

/**
 * The Active selection as the island reads it — the donor's public
 * `activeSelectionChangedEvent` payload.
 */
export interface IslandActiveSelection {
  selectionId: number | null;
  lineId: number | null;
  /** The selection's served slug — the linked item's category, else its own
   *  snapshot (ADR-0012 decision 4 as amended) — or `""`. */
  categorySlug: string;
  /** The selection's item pointer as stored. */
  itemId: number | null;
  isPerPlan: boolean;
  isFinalized: boolean;
  /** The selection's own deleted flag — the donor's `!!selection.deletedAt`. */
  isSoftDeleted: boolean;
}

/** No active row: the donor's initial state (`observers.ts:27-32`). */
export const NO_ACTIVE_SELECTION: IslandActiveSelection = {
  selectionId: null,
  lineId: null,
  categorySlug: "",
  itemId: null,
  isPerPlan: false,
  isFinalized: false,
  isSoftDeleted: false,
};

/**
 * The payload of a resolved active row (`resolveActiveRow`): a header row gives
 * its line and nothing of a selection, as the donor's `payload.selection` is
 * `undefined` there. With `itemCleared` — Set as Per Plan's emit, the
 * selection with `itemId: null` (`LineMoreActions/actions.ts:448-458`, #861
 * D3 a) — it reads no item; every other field is still the cache's.
 */
export function islandActiveSelection(
  row: {
    line: { id: number; status: string; perPlan: boolean };
    selection: {
      id: number;
      deleted: boolean;
      categorySlug: string | null;
      itemId: number | null;
    } | null;
  } | null,
  { itemCleared = false }: { itemCleared?: boolean } = {},
): IslandActiveSelection {
  if (!row) return NO_ACTIVE_SELECTION;
  const { line, selection } = row;
  return {
    selectionId: selection?.id ?? null,
    lineId: line.id,
    categorySlug: selection?.categorySlug ?? "",
    itemId: itemCleared ? null : (selection?.itemId ?? null),
    isPerPlan: line.perPlan,
    isFinalized: line.status === "finalized",
    isSoftDeleted: selection?.deleted ?? false,
  };
}

/**
 * The category write (`observers.ts:41-58`): nothing when the slug is the one
 * the URL already holds; else set it, or delete the param for `""` — dropping a
 * category the user picked, as the donor does (#767 R1). Always `replace`.
 */
export function categoryWrite(
  current: string,
  next: string,
): { category: string | undefined } | null {
  if (next === current) return null;
  return { category: next || undefined };
}

/** What the self-heal reads (`catalogCategoryFallback.ts`). */
export interface EmptyCategoryFallbackArgs {
  allResultsLoaded: boolean;
  itemCount: number;
  category: string;
  search: string;
  mfrs: string;
  tiers: string;
  status: string;
}

/**
 * The self-heal, verbatim: a category that is the only narrowing and returns
 * nothing, once the search has settled, is dropped — the spec editor's
 * auto-applied line category can name one the catalog has no items in.
 */
export function shouldClearEmptyCategoryFilter({
  allResultsLoaded,
  itemCount,
  category,
  search,
  mfrs,
  tiers,
  status,
}: EmptyCategoryFallbackArgs): boolean {
  if (!allResultsLoaded) return false;
  if (itemCount > 0) return false;
  if (!category) return false;
  return !search && !mfrs && !tiers && !status;
}

/**
 * The donor's `allResultsLoaded` (`ItemSearchIsland.tsx:104-109`): not reading
 * a new query, not reading a next page, not failed. Its fourth arm —
 * `isFetchingFromCache`, a cached result shown for a category the URL no longer
 * names — has no counterpart: the host's query is the URL's, synchronously.
 */
export function searchSettled({
  isFetchingSearch,
  isFetchingNextPage,
  isError,
}: {
  isFetchingSearch: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
}): boolean {
  return !isFetchingSearch && !isFetchingNextPage && !isError;
}

/** The card gates, the donor's expressions (`ItemSearchIsland.tsx:150-158`). */
export function islandGates(active: IslandActiveSelection): {
  enableCardActions: boolean;
  enableCardCTAs: boolean;
} {
  return {
    enableCardActions: !active.isFinalized && !active.isSoftDeleted,
    enableCardCTAs:
      !active.isFinalized &&
      !active.isPerPlan &&
      active.selectionId !== null &&
      !active.isSoftDeleted,
  };
}

/**
 * The two messages (`InfoAlert.tsx`), verbatim. The donor's string continues
 * its line with a backslash, so the next line's indentation lands in the text
 * and HTML collapses it to the one space kept here.
 */
export const ISLAND_ALERTS = {
  finalized:
    "You cannot add or make changes to a Specification Line marked as 'Finalized'. Change the Line status before you can make changes to the Specification Items.",
  perPlan:
    "Items cannot be added to Specification Lines that are set as 'Per Plan'. Uncheck 'Per Plan' option in the Specification Line menu to add a item Selection.",
} as const;

/**
 * Which message shows: finalized first, then per plan. A struck selection is
 * not consulted — the donor's effect reads only those two flags.
 */
export function islandAlert(active: IslandActiveSelection): string | null {
  if (active.isFinalized) return ISLAND_ALERTS.finalized;
  if (active.isPerPlan) return ISLAND_ALERTS.perPlan;
  return null;
}

/**
 * The grid's item order (`ItemSearchIsland.tsx:212-262`): the pinned card, the
 * results less its id, then the last requested item through #798's gate.
 */
export function islandGridItems<T extends { id: number }>({
  pinned,
  results,
  requested,
  allResultsLoaded,
}: {
  pinned: T | null;
  results: readonly T[];
  requested: T | null;
  allResultsLoaded: boolean;
}): { pinned: T | null; results: T[]; appended: T | null } {
  const shown = results.filter((item) => item.id !== pinned?.id);
  const appended =
    requested !== null &&
    showsLastRequestedItem({
      allResultsLoaded,
      requestedItemId: requested.id,
      activeItemId: pinned?.id ?? null,
      resultItemIds: results.map((item) => item.id),
    })
      ? requested
      : null;
  return { pinned, results: shown, appended };
}

/**
 * `?mfrs=` / `?tiers=` — space-separated ids, the donor's spelling
 * (`itemsApi.ts:276-282` splits on a space). What is not a positive id is
 * dropped, repeats collapse, and nothing left is no filter at all.
 */
export function islandIdList(param: string | undefined): number[] | undefined {
  if (!param) return undefined;
  const ids = param
    .split(" ")
    .filter(Boolean)
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  return ids.length > 0 ? [...new Set(ids)] : undefined;
}

/** The island's URL keys, as the edit route's schema reads them. */
export type IslandSearch = Pick<
  SpecEditSearch,
  "itemSearch" | "category" | "mfrs" | "tiers" | "status" | "page"
>;

/**
 * One page of the island's search: the term, the category (a subtree, by
 * slug), the manufacturers and the tiers (`ItemSearchIsland.tsx:83-90`) — never
 * `status`, which the trigger does not send. No `order_by`, as the donor sends
 * none: the list read's default ordering, relevance with a term.
 */
export function islandListInput(search: IslandSearch, page: number): ListCatalogItemsBrowserInput {
  const term = normalizeCatalogSearchTerm(search.itemSearch);
  const manufacturerIds = islandIdList(search.mfrs);
  const tierIds = islandIdList(search.tiers);
  return {
    page,
    pageSize: ISLAND_PAGE_SIZE,
    sort: DEFAULT_CATALOG_SORT,
    ...(term === undefined ? {} : { search: term }),
    ...(search.category ? { categorySlug: search.category } : {}),
    ...(manufacturerIds === undefined ? {} : { manufacturerIds }),
    ...(tierIds === undefined ? {} : { tierIds }),
    // falcon's search items carry every image, so the CTA dialog draws its
    // thumbnail strip on its first frame (#964 N16).
    includeImages: true,
  };
}

/** The item's category slug, or `""` — what the donor's Select re-emits. */
export function itemCategorySlug(item: {
  category?: { name?: string; slug?: string } | null;
}): string {
  return item.category?.slug ?? "";
}

/**
 * What an island write may set — the keys as text, the donor's
 * `params.set(key, value)`; `undefined` deletes one. `page` is written and
 * never read (#767 R1). The route spells the text into the URL
 * (`bareIslandSearch`).
 */
export type IslandSearchPatch = Partial<IslandSearch>;

/** The donor's `params.set('page', '1')` — the category, manufacturer and tier-chip writes' rewind. */
const FIRST_PAGE = { page: "1" } as const;

/**
 * The Category filter's write (`CategoryFilter.tsx:36-70`): nothing when the
 * pick is the slug the URL holds; else set it, or delete it for `All
 * Categories` — and either way rewind to page 1.
 */
export function categoryFilterWrite(current: string, next: string): IslandSearchPatch | null {
  if (next === current) return null;
  return { category: next || undefined, ...FIRST_PAGE };
}

interface NamedFilter {
  id: number;
  name: string;
}

interface TierFilter extends NamedFilter {
  value: number;
}

/**
 * The chosen filters the URL names, in the URL's order, each resolved through
 * `byId` — an id it cannot resolve shows nothing, as the donor's
 * `.map(id => cache[id]).filter(Boolean)` drops it
 * (`ManufacturersFilter.tsx:56-64`, `TiersFilter.tsx:96-103`).
 */
export function resolveIslandFilter<T>(
  param: string | number | undefined,
  byId: ReadonlyMap<number, T>,
): T[] {
  const ids = islandIdList(param === undefined ? undefined : String(param)) ?? [];
  return ids.flatMap((id) => {
    const found = byId.get(id);
    return found === undefined ? [] : [found];
  });
}

/** The ids space-joined, the donor's spelling; none deletes the key. */
const idsParam = (rows: readonly { id: number }[]) =>
  rows.length === 0 ? undefined : rows.map((row) => row.id).join(" ");

/**
 * A manufacturer pick (`handleAddManufacturerFilter`): a repeat is ignored;
 * else the chosen list, sorted by name, and page 1.
 */
export function addManufacturerWrite(
  selected: readonly NamedFilter[],
  picked: NamedFilter,
): IslandSearchPatch | null {
  if (selected.some((row) => row.id === picked.id)) return null;
  const next = [...selected, picked].sort((a, b) => a.name.localeCompare(b.name));
  return { mfrs: idsParam(next), ...FIRST_PAGE };
}

/**
 * A manufacturer chip's removal (`handleRemoveManufacturerFilter`): the
 * resolved list less that one, the key deleted when it empties, and page 1.
 */
export function removeManufacturerWrite(
  selected: readonly NamedFilter[],
  removedId: number,
): IslandSearchPatch {
  return { mfrs: idsParam(selected.filter((row) => row.id !== removedId)), ...FIRST_PAGE };
}

/**
 * A tier pick (`handleSelectTierFilterValue`): a repeat is ignored; else the
 * chosen tiers' **ids** in their `value` order — id and value part ways for
 * the newer EI tiers — and no `page` write.
 */
export function addTierWrite(
  selected: readonly TierFilter[],
  picked: TierFilter,
): IslandSearchPatch | null {
  if (selected.some((row) => row.id === picked.id)) return null;
  const next = [...selected, picked].sort((a, b) => a.value - b.value);
  return { tiers: idsParam(next) };
}

/**
 * A tier chip's removal (`handleRemoveTierFilter`): the rest, the key deleted
 * when it empties, and page 1 — the rewind `a86c6e8b` added, so a narrowed
 * result set is not stranded past its last page. A pick still writes no page.
 */
export function removeTierWrite(
  selected: readonly TierFilter[],
  removedId: number,
): IslandSearchPatch {
  return { tiers: idsParam(selected.filter((row) => row.id !== removedId)), ...FIRST_PAGE };
}

/**
 * An island write as the options a router navigate takes: the keys merged into
 * the current search — the editor's `specSearch` and every other key kept, as
 * the donor's functional `setSearchParams` keeps them — and the entry
 * replaced, never pushed (`observers.ts`, `SearchInput.tsx`: `{ replace: true }`).
 */
export function islandSearchNavigation(patch: IslandSearchPatch) {
  return {
    search: <Search extends IslandSearch>(previous: Search) => ({ ...previous, ...patch }),
    replace: true,
  };
}

/**
 * The edit route's search middleware: `bareIslandSearch` over the search the
 * route's validation built. Exported so the router-level test drives exactly
 * what the route runs (`item-search-island.router.browser.test.tsx`).
 */
export function islandRouteSearchMiddleware<Search extends object>({
  search,
  next,
}: {
  search: Search;
  next: (search: Search) => Search;
}): Search {
  return bareIslandSearch(next(search));
}

/** The island's keys the router writes as JSON — every one but the term. */
const STRUCTURED_KEYS = ["category", "mfrs", "tiers", "status", "page"] as const;

/**
 * The edit route's search as its writer must be handed it: each structured
 * key's number-like text bare again.
 *
 * The router validates the search it builds before it stringifies it
 * (`buildLocation`'s validate middleware), and the route's schema reads
 * `mfrs`, `tiers`, `page`, `category` and `status` as text — so `?mfrs=58`
 * and `?page=1` reach the writer as `"58"` and `"1"`, which it JSON-quotes:
 * `?mfrs=%2258%22&page=%221%22`. The route runs this as its own search
 * middleware, which wraps that validation. A value is un-quoted only when it
 * is exactly the spelling of a number or a boolean, so `58`, `1` and `58 99`
 * read as the donor's `URLSearchParams` writes them. A hand-typed value JSON
 * parses to something else (`1.0`, `1e2`, `null`) stays text and is written
 * quoted — no number spells it back. The typed terms are the codec's literal
 * params and never reach JSON.
 */
export function bareIslandSearch<Search extends object>(search: Search): Search {
  const next = { ...search } as Record<string, unknown>;
  for (const key of STRUCTURED_KEYS) {
    const value = next[key];
    if (typeof value !== "string") continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      continue;
    }
    if ((typeof parsed === "number" || typeof parsed === "boolean") && String(parsed) === value) {
      next[key] = parsed;
    }
  }
  return next as Search;
}
